import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Lightbox from '../components/Lightbox';
import ContactForm from '../components/ContactForm';
import ProductCard from '../components/ProductCard';
import { fetchCollection, fetchLocalizedCollection, entryMatchesSlug } from '../lib/cms';
import { imageUrl, srcSet } from '../lib/images';

// Category fallbacks for newly added and re-organized categories
const CATEGORY_FALLBACKS = {
  panelbukker: {
    titel: 'Panelbukker',
    seo_titel: 'PMB | Panelbukker – Automatisk og fleksibel panelbukning',
    meta_beskrivelse: 'Automatiske panelbukkere til hurtig og præcis bukning af tyndpladeemner, kassetter og paneler.',
    kort_beskrivelse: 'Automatiseret panelbukning med ultrahurtig omstilling, høj gentagelsesnøjagtighed og minimal manuel håndtering.',
    billede: ['https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/mvd-e-series.webp'],
    lang_beskrivelse: '<p>Panelbukkere repræsenterer fremtidens fleksible pladeformning. Med en automatisk panelbukker bukkes alle fire sider af et emne i én uafbrudt proces, uden behov for manuel vending eller specialværktøjsskift mellem hvert buk.</p><h3>Fordele ved en automatisk panelbukker</h3><ul><li><strong>Ekstrem høj hastighed:</strong> Betydeligt hurtigere end traditionelle kantpressere ved kassetter og større emner.</li><li><strong>Automatisk værktøjsindstilling:</strong> Universelle bukkeværktøjer tilpasser sig automatisk emnets dimensioner.</li><li><strong>Skånsom overfladebehandling:</strong> Ideel til rustfrit stål, malede eller slebne overflader uden ridser.</li><li><strong>Maksimal ergonomi og sikkerhed:</strong> Operatøren skal blot ilægge pladen – maskinen håndterer resten.</li></ul>',
    cta_tekst: 'Kontakt os om panelbukkere',
    cta_link: '/kontakt',
  },
  'hydraulisk-presse': {
    titel: 'Hydraulisk presse',
    seo_titel: 'PMB | Hydrauliske presser – Kraft, præcision og dybtræk',
    meta_beskrivelse: 'Industrielle hydrauliske presser til formning, dybtræk og stansning i metalindustrien.',
    kort_beskrivelse: 'Kraftfulde hydrauliske produktionspresser fra førende producenter som Hidroliksan med trinløs tryk- og hastighedsstyring.',
    billede: ['https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/presser.png'],
    lang_beskrivelse: '<p>PMB forhandler kraftfulde hydrauliske presser til industriel formgivning, dybtræk, prægning og montage. Vores hydrauliske presser udmærker sig ved ekstrem stivhed i rammekonstruktionen, præcis positionering og fleksibel programmering af slaglængde og trykprofil.</p><h3>Anvendelsesområder og fordele</h3><ul><li><strong>Præcis trykregulering:</strong> Fuldt programmerbar trykkurve og holdetid under tryk.</li><li><strong>Alsidighed:</strong> Ideel til alt fra prototypeudvikling til kontinuerlig serieproduktion af krævende emner.</li><li><strong>Robust konstruktion:</strong> Dimensioneret til mange års intensiv 3-holdsdrift med minimal vedligeholdelse.</li></ul>',
    cta_tekst: 'Kontakt os om hydrauliske presser',
    cta_link: '/kontakt',
  },
  'mekanisk-presse': {
    titel: 'Mekanisk presse',
    seo_titel: 'PMB | Mekaniske presser & Excenterpresser',
    meta_beskrivelse: 'Robuste mekaniske excenterpresser og produktionspresser til hurtig stansning og serieproduktion.',
    kort_beskrivelse: 'Klassiske og moderne mekaniske presser med høj slaghastighed og uovertruffen driftssikkerhed gennem generationer.',
    billede: ['https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/maskiner.jpg'],
    lang_beskrivelse: '<p>Mekaniske presser og excenterpresser har været kernen i PMBs virke i mere end et århundrede. De leverer uovertruffen hastighed og stivhed ved stansning, klipning og formning af metalemner i mellemstore og store seriestørrelser.</p><h3>Egenskaber ved mekaniske presser</h3><ul><li><strong>Høj produktivitet:</strong> Hurtige takter og stor kinetisk energi pr. slag.</li><li><strong>Høj præcision:</strong> Præcise føringer og afbalancerede svinghjul sikrer ensartet snit- og formkvalitet.</li><li><strong>Lang levetid:</strong> En mekanisk presse fra PMB er bygget til at køre pålideligt i årtier med god service.</li></ul>',
    cta_tekst: 'Få rådgivning om mekaniske presser',
    cta_link: '/kontakt',
  },
  'styringer-og-software': {
    titel: 'Styringer og software',
    seo_titel: 'PMB | Maskinstyringer & Industrisoftware',
    meta_beskrivelse: 'Moderne CNC-styringer, sikkerhedsmoduler og produktionssoftware til optimering af presselinjer og pladebearbejdning.',
    kort_beskrivelse: 'Intelligente CNC-styringer, retrofit-løsninger og Industry 4.0 software til optimering af produktion og oppetid.',
    billede: ['https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/fremforing-hero.webp'],
    lang_beskrivelse: '<p>Fremtidens produktion kræver intuitive, sikre og opkoblede styringer. Hos PMB tilbyder vi både opgradering (retrofitting) af ældre maskinstyringer og levering af moderne CNC-systemer til nye anlæg.</p><h3>Vores styrings- og softwareløsninger</h3><ul><li><strong>Retrofit af pressestyringer:</strong> Udskiftning af forældede relæ- og PLC-styringer med moderne touchscreen-styringer.</li><li><strong>Sikkerhedsopgradering:</strong> Integrering af moderne lysgitre, to-håndsbetjening og CE-overensstemmelse.</li><li><strong>Procesovervågning:</strong> Realtidsovervågning af pressekraft, værktøjsbelastning og cyklustider.</li></ul>',
    cta_tekst: 'Kontakt os om styring og software',
    cta_link: '/kontakt',
  },
  'slibe-og-afgratemaskiner': {
    titel: 'Slibe- og afgratemaskiner',
    seo_titel: 'PMB | Slibe- og afgratemaskiner til metalbearbejdning',
    meta_beskrivelse: 'Professionelle slibe- og afgratemaskiner til fjernelse af grater, kantafronding og ensartet overfladefinish.',
    kort_beskrivelse: 'Industrielle afgratere og slibemaskiner til kantafronding, oxidfjernelse og flot overfladefinish på skårne metalemner.',
    billede: ['https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/maskiner.jpg'],
    lang_beskrivelse: '<p>Efter laserskæring, plasmaskæring eller stansning er afgratning og kantafronding afgørende for sikker håndtering og korrekt vedhæftning ved efterfølgende pulverlakering eller galvanisering.</p><h3>Fordele ved moderne afgratning</h3><ul><li><strong>Ensartet kantafronding:</strong> Fjerner skarpe kanter og grater på både inder- og yderkonturer.</li><li><strong>Oxidfjernelse:</strong> Renser laserskårne kanter for oxidlag, så maling og lak hæfter optimalt.</li><li><strong>Høj kapacitet:</strong> Gennemløbsmaskiner behandler hundredvis af emner i timen.</li></ul>',
    cta_tekst: 'Kontakt os om slibe- og afgratemaskiner',
    cta_link: '/kontakt',
  },
  pladeklipning: {
    titel: 'Pladeklipning',
    seo_titel: 'PMB | Pladeklipning – Guillotinesakse & Skærelinjer',
    meta_beskrivelse: 'Præcise guillotinesakse og klippemaskiner til skæring af tynd- og sværplade i stål og metaller.',
    kort_beskrivelse: 'Effektive guillotinesakse fra MVD iShear-serien. Rene snit uden grater og minimal deformation uanset pladetykkelse.',
    billede: ['https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/mvd-ishear-guillotine-shear.png'],
    lang_beskrivelse: '<p>MVD er en internationalt anerkendt maskinproducent med årtiers erfaring inden for pladebearbejdning. Som officiel distributør i Danmark leverer PMB rådgivning, installation og service på MVDs markedsledende <strong>iShear</strong> pladesakse til ren og præcis pladeklipning.</p>',
    cta_tekst: 'Kontakt os om pladeklipning',
    cta_link: '/kontakt',
  },
};

export default function ProductDetailPage({ specificSlug }) {
  const params = useParams();
  const currentSlug = specificSlug || params.slug;

  const [product, setProduct] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState('ALL');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setSelectedManufacturer('ALL');

    Promise.all([
      fetchLocalizedCollection('ydelser_produkter', 'da'),
      fetchLocalizedCollection('produkter', 'da'),
    ]).then(([areas, machineProducts]) => {
      setAllProducts(areas || []);

      // Smart slug alias lookup
      const slugAliases = {
        'pladeklipning': ['pladeklipning', 'saks'],
        'saks': ['saks', 'pladeklipning'],
        'slibe-og-afgratemaskiner': ['slibe-og-afgratemaskiner', 'slibemaskiner'],
        'slibemaskiner': ['slibemaskiner', 'slibe-og-afgratemaskiner'],
        'kantpresse': ['kantpresse', 'kantpresser', 'kantbukker'],
        'kantpresser': ['kantpresser', 'kantpresse', 'kantbukker'],
        'kantbukker': ['kantbukker', 'kantpresser', 'kantpresse'],
        'coilanlaeg': ['coilanlaeg', 'coilanlaeg-og-foelgeudstyr', 'udstyr', 'rettevaerk'],
        'coilanlaeg-og-foelgeudstyr': ['coilanlaeg-og-foelgeudstyr', 'coilanlaeg', 'udstyr'],
        'fiberlaser': ['fiberlaser', 'laser'],
        'laser': ['laser', 'fiberlaser'],
        'hydraulisk-presse': ['hydraulisk-presse', 'presser'],
        'mekanisk-presse': ['mekanisk-presse', 'presser'],
      };

      const candidateSlugs = slugAliases[currentSlug] || [currentSlug];

      let matched = (areas || []).find((item) =>
        candidateSlugs.some((s) => entryMatchesSlug(item, s, 'da'))
      );

      // If not in CMS yet, use high quality fallback category data
      if (!matched && CATEGORY_FALLBACKS[currentSlug]) {
        matched = {
          ...CATEGORY_FALLBACKS[currentSlug],
          id: `fallback-${currentSlug}`,
          slug: currentSlug,
        };
      }

      setProduct(matched || null);

      // Find machinery products belonging to this category
      const matchedCategoryProducts = (machineProducts || []).filter((item) => {
        const catTerms = item.terms?.produktkategori || [];
        const catField = Array.isArray(item.kategori) ? item.kategori : [item.kategori];
        return (
          candidateSlugs.some((s) => catTerms.includes(s) || catField.includes(s)) ||
          ((currentSlug === 'kantpresser' || currentSlug === 'kantpresse') &&
            (catTerms.includes('kantpresser') || catTerms.includes('kantpresse') || !catTerms.length))
        );
      });
      setCategoryProducts(matchedCategoryProducts);

      if (matched && matched.galleri_slug) {
        fetchCollection('galleri').then((galleries) => {
          const matchedGal = (galleries || []).find((g) => entryMatchesSlug(g, matched.galleri_slug, 'da'));
          setGallery(matchedGal || null);
        });
      } else {
        setGallery(null);
      }
      setLoading(false);
    });
  }, [currentSlug]);

  // Dynamically extract manufacturers existing in this category
  const availableManufacturers = useMemo(() => {
    const set = new Set();
    categoryProducts.forEach((p) => {
      const name = p.producent_tekst || p.terms?.producent?.[0] || '';
      if (name) set.add(name);
    });
    return Array.from(set);
  }, [categoryProducts]);

  // Filter products by selected manufacturer
  const filteredProducts = useMemo(() => {
    if (selectedManufacturer === 'ALL') return categoryProducts;
    return categoryProducts.filter((p) => {
      const name = p.producent_tekst || p.terms?.producent?.[0] || '';
      return name.trim().toLowerCase() === selectedManufacturer.trim().toLowerCase();
    });
  }, [categoryProducts, selectedManufacturer]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-stone-50">
        <div className="flex items-center gap-3 text-stone-600">
          <i className="fa-solid fa-circle-notch fa-spin text-xl text-[#006368]" />
          <span>Indlæser kategori...</span>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-stone-50 px-4 text-center">
        <h1 className="text-2xl font-bold text-stone-900">Ydelse eller produktområde blev ikke fundet</h1>
        <p className="text-stone-600 mt-2 text-sm">Den efterspurgte kategori kunne ikke indlæses.</p>
        <Link
          to="/"
          className="mt-6 px-6 py-2.5 rounded bg-[#006368] text-white font-semibold text-sm hover:bg-[#004d51] transition"
        >
          Gå til forsiden
        </Link>
      </div>
    );
  }

  const mainImage = Array.isArray(product.billede) ? product.billede[0] : product.billede;
  const heroImage =
    (Array.isArray(product.hero_billede) && product.hero_billede.length > 0)
      ? product.hero_billede[0]
      : (typeof product.hero_billede === 'string' && product.hero_billede ? product.hero_billede : null);
  const galleryImages = gallery && Array.isArray(gallery.billeder) ? gallery.billeder : [];

  return (
    <>
      <Helmet>
        <title>{product.seo_titel || `${product.titel} - POVL MØLLERS MASKINFABRIK A/S`}</title>
        {product.meta_beskrivelse && (
          <meta name="description" content={product.meta_beskrivelse} />
        )}
        <meta property="og:title" content={product.seo_titel || product.titel} />
        {product.meta_beskrivelse && (
          <meta property="og:description" content={product.meta_beskrivelse} />
        )}
        {mainImage && <meta property="og:image" content={mainImage} />}
      </Helmet>

      {/* Header breadcrumb bar */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="hover:text-[#006368] transition">
            Forside
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="text-stone-500">Produktkategorier</span>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">{product.titel}</span>
        </div>
      </div>

      {/* Hero Banner for Category */}
      <section className="relative bg-[#1a2024] text-white py-16 sm:py-24 border-b border-stone-800 overflow-hidden">
        {heroImage && (
          <>
            <img
              src={imageUrl(heroImage, 'width=1920,quality=80,format=auto')}
              srcSet={srcSet(heroImage, [800, 1200, 1920])}
              sizes="100vw"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none"
            />
            {/* Mørkt overlay: afbalanceret så billedet træder flot frem, mens teksten forbliver 100% skarp og læsbar */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a2024]/80 via-[#1a2024]/65 to-[#1a2024]/40 pointer-events-none" />
          </>
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <span className="inline-block px-3 py-1 rounded bg-[#006368] text-white text-xs font-bold uppercase tracking-wider mb-4">
                Produktkategori
              </span>
              <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
                {product.titel}
              </h1>
              {product.kort_beskrivelse && (
                <p className="mt-4 text-base sm:text-lg text-stone-300 leading-relaxed max-w-2xl">
                  {product.kort_beskrivelse}
                </p>
              )}

              <div className="mt-8 flex flex-wrap items-center gap-4">
                {categoryProducts.length > 0 && (
                  <a
                    href="#produkter-grid"
                    className="px-6 py-3.5 rounded bg-[#006368] hover:bg-[#004d51] text-white font-bold text-sm tracking-wide transition shadow-sm flex items-center gap-2"
                  >
                    <span>Se maskiner ({categoryProducts.length})</span>
                    <i className="fa-solid fa-arrow-down text-xs" />
                  </a>
                )}
                <a
                  href="#kontakt-sektion"
                  className="px-6 py-3.5 rounded bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition whitespace-nowrap flex items-center gap-2"
                >
                  <i className="fa-solid fa-envelope text-[#0d8086]" />
                  <span>Få uforpligtende rådgivning</span>
                </a>
                <a
                  href="tel:57526262"
                  className="px-6 py-3.5 rounded bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition whitespace-nowrap flex items-center gap-2"
                >
                  <i className="fa-solid fa-phone text-[#0d8086]" />
                  <span className="whitespace-nowrap">Ring 57 52 62 62</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Column: Description & Products Grid */}
            <div className="lg:col-span-8 space-y-12">
              {/* Category Introduction / Editorial text */}
              {product.lang_beskrivelse && (
                <div
                  className="prose prose-stone max-w-none text-stone-800 leading-relaxed [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:text-stone-900 [&>h2]:mt-8 [&>h2]:mb-4 [&>h3]:text-xl [&>h3]:font-bold [&>h3]:text-stone-900 [&>h3]:mt-6 [&>h3]:mb-3 [&>p]:mb-4 [&>p]:text-base [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:space-y-2 [&>ul]:mb-6 [&>strong]:text-stone-900"
                  dangerouslySetInnerHTML={{ __html: product.lang_beskrivelse }}
                />
              )}

              {/* PRODUCTS SECTION FOR THIS CATEGORY */}
              {categoryProducts.length > 0 && (
                <div id="produkter-grid" className="pt-8 border-t border-stone-200 scroll-mt-20">
                  {/* Header & Dynamic Manufacturer Filter */}
                  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wider text-[#006368] mb-1 block">
                        Katalog
                      </span>
                      <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900">
                        Maskiner & modeller
                      </h2>
                      <p className="text-sm text-stone-600 mt-1">
                        Find den rette maskine til din produktion. Klik på et produkt for tekniske specifikationer.
                      </p>
                    </div>

                    {/* Manufacturer Filter */}
                    {availableManufacturers.length > 0 && (
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <span className="text-xs font-bold text-stone-500 uppercase tracking-wider">
                          Producent:
                        </span>
                        <div className="flex flex-wrap items-center gap-1.5 p-1 bg-stone-100 rounded-lg border border-stone-200">
                          <button
                            type="button"
                            onClick={() => setSelectedManufacturer('ALL')}
                            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                              selectedManufacturer === 'ALL'
                                ? 'bg-[#006368] text-white shadow-sm'
                                : 'text-stone-700 hover:text-stone-900 hover:bg-stone-200/70'
                            }`}
                          >
                            Alle ({categoryProducts.length})
                          </button>
                          {availableManufacturers.map((m) => {
                            const count = categoryProducts.filter((p) => {
                              const name = p.producent_tekst || p.terms?.producent?.[0] || '';
                              return name.trim().toLowerCase() === m.trim().toLowerCase();
                            }).length;

                            return (
                              <button
                                key={m}
                                type="button"
                                onClick={() => setSelectedManufacturer(m)}
                                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                                  selectedManufacturer.toLowerCase() === m.toLowerCase()
                                    ? 'bg-[#006368] text-white shadow-sm'
                                    : 'text-stone-700 hover:text-stone-900 hover:bg-stone-200/70'
                                }`}
                              >
                                {m} ({count})
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Product Grid */}
                  {filteredProducts.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {filteredProducts.map((item) => (
                        <ProductCard key={item.id} product={item} lang="da" />
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center bg-stone-50 rounded-lg border border-stone-200">
                      <p className="text-stone-600 text-sm">
                        Ingen maskiner fundet for producenten "{selectedManufacturer}".
                      </p>
                      <button
                        type="button"
                        onClick={() => setSelectedManufacturer('ALL')}
                        className="mt-3 text-xs font-bold text-[#006368] underline"
                      >
                        Nulstil filter og vis alle maskiner
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Contact section on-page */}
              <div id="kontakt-sektion" className="pt-10 border-t border-stone-200">
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-stone-900">
                    Forespørgsel på {product.titel.toLowerCase()}
                  </h2>
                  <p className="text-sm text-stone-600 mt-1">
                    Har du spørgsmål til dimensionering, levering eller tekniske muligheder? Kontakt vores eksperter.
                  </p>
                </div>
                <ContactForm defaultSubject={`Forespørgsel vedr. ${product.titel}`} />
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="lg:col-span-4 space-y-8">
              {/* Other Product Categories list */}
              <div className="bg-stone-50 rounded-lg border border-stone-200 p-6">
                <h3 className="text-sm font-bold uppercase tracking-wider text-stone-900 mb-4 flex items-center gap-2">
                  <i className="fa-solid fa-layer-group text-[#006368]" />
                  <span>Flere produktområder</span>
                </h3>
                <div className="space-y-1">
                  {allProducts.map((item) => {
                    const itemSlug = typeof item.slug === 'object' ? item.slug._ || item.slug.da : item.slug;
                    const isActive = itemSlug === currentSlug;
                    return (
                      <Link
                        key={item.id}
                        to={`/${itemSlug}`}
                        className={`block px-3 py-2.5 rounded text-sm font-medium transition ${
                          isActive
                            ? 'bg-[#006368] text-white font-bold'
                            : 'text-stone-700 hover:bg-stone-200/60 hover:text-[#006368]'
                        }`}
                      >
                        {item.titel}
                      </Link>
                    );
                  })}
                  <Link
                    to="/leverandoerer"
                    className="block px-3 py-2.5 rounded text-sm font-medium text-stone-700 hover:bg-stone-200/60 hover:text-[#006368] transition"
                  >
                    Leverandører
                  </Link>
                </div>
              </div>

              {/* Quick Contact Card */}
              <div className="bg-[#1a2024] text-white rounded-lg p-6 space-y-4">
                <h3 className="font-bold text-base text-white">Brug for hurtig rådgivning?</h3>
                <p className="text-sm text-stone-300">
                  Vores teknikere sidder klar ved telefonen til at besvare dine tekniske spørgsmål.
                </p>
                <div className="pt-2 space-y-3">
                  <a
                    href="tel:57526262"
                    className="flex items-center gap-3 p-3 rounded bg-white/10 hover:bg-white/20 transition text-sm font-bold whitespace-nowrap"
                  >
                    <i className="fa-solid fa-phone text-[#0d8086]" />
                    <span className="whitespace-nowrap">57 52 62 62</span>
                  </a>
                  <a
                    href="mailto:info@pmborup.dk"
                    className="flex items-center gap-3 p-3 rounded bg-white/10 hover:bg-white/20 transition text-sm font-medium whitespace-nowrap"
                  >
                    <i className="fa-solid fa-envelope text-[#0d8086]" />
                    <span className="whitespace-nowrap">info@pmborup.dk</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <Lightbox
        images={galleryImages.length > 0 ? galleryImages : mainImage ? [mainImage] : []}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
}
